import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { MEDIA_SIZE_SM } from "@/core/media";
import { formatFullName } from "@/data/persons";
import { IWOConfirmTask, WOConfirmTaskActionType } from "@/data/tasks";
import { Flex } from "@/eworkitComponents/components";
import { WorkOrderSubHeader } from "@/views/components/WorkOrder";

import { AcceptTaskModalButton, RejectTaskModalButton } from "../../ConfirmationModals";
import { TaskPriorityLabel } from "../../TaskPriorityLabel";

export interface IWOCHeaderProps {
  task: IWOConfirmTask;
  onAction?: (actionType: WOConfirmTaskActionType, comment: string, task: IWOConfirmTask) => void;
}

export const WOCHeader: FC<IWOCHeaderProps> = memo(({ task, onAction, ...props }) => {
  const { t } = useTranslation("session");
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);
  const workOrder = task?.entity;

  return (
    <Section
      width={12}
      direction="column"
      background="background-card"
      shadow={isSmallScreen ? "none" : "default"}
      borderRadius={!isSmallScreen}
      padding={["md", "lg"]}
      innerBorder
      {...props}>
      <Flex padding={[false, false, "sm", false]} justify="space-between" innerMargin="sm">
        <TaskPriorityLabel priority={task.priority} />
      </Flex>

      <Flex
        direction={isSmallScreen ? "column" : "row"}
        padding={["md", false, false, false]}
        innerMargin="md"
        justify="space-between">
        <Flex innerMargin="md">
          <Icon size="xxl" icon="file-tasks-1" />

          <Flex direction="column" innerMargin="sm">
            <Header weight="bold" tagType="h1">
              {formatFullName(workOrder.consultantProfile)}
            </Header>
            <WorkOrderSubHeader entity={workOrder} />
          </Flex>
        </Flex>

        <Flex innerMargin="sm">
          <RejectTaskModalButton
            display={isSmallScreen ? "block" : "inline-block"}
            variant="secondary"
            size="lg"
            onReject={(comment: string) => onAction?.("reject", comment, task)}>
            {t("TASKS.DETAILS.BUTTON_REJECT")}
          </RejectTaskModalButton>
          <AcceptTaskModalButton
            display={isSmallScreen ? "block" : "inline-block"}
            variant="primary"
            size="lg"
            onAccept={(comment: string) => onAction?.("accept", comment, task)}>
            {t("TASKS.DETAILS.BUTTON_CONFIRM")}
          </AcceptTaskModalButton>
        </Flex>
      </Flex>
    </Section>
  );
});
